import { useEffect, useMemo, useRef, useState } from "react";
import polyline from "google-polyline";
import { Feature, Polygon } from "geojson";

import { useRestClientContext } from "../contexts/RestClientContext";
import { useTeamContext } from "../contexts/TeamContext";
import { Properties } from "../rest/responses/Properties";
import { Accessor, Filter, QueryFilter, Scalar, V3PropertyFilter } from "../types/types";

import useBatchedProperties, { mapAccessors, mapPropertyAttributes } from "./useBatchedProperties";

export interface UseFilteredFeatureCollectionProps {
	address?: string;
	groups?: number[];
	grades?: { [key: string]: boolean };
	useLodgedGrades?: boolean;
	thermalScores?: { [key: string]: boolean };
	polygons?: Feature<Polygon>[];
	detachment?: string | [];
	propertyType?: string | [];
	fuelType?: string | [];
	roofType?: number | [];
	wallType?: string | [];
	analysis?: string[];
	triggerUpdate?: number;
	hasImages?: string;
}

export default function useFilteredFeatureCollection({
	address,
	groups,
	grades,
	useLodgedGrades,
	thermalScores,
	polygons,
	detachment,
	fuelType,
	propertyType,
	roofType,
	wallType,
	analysis,
	hasImages,
}: UseFilteredFeatureCollectionProps) {
	const abortControllerRef = useRef<AbortController>();

	const client = useRestClientContext();
	const team = useTeamContext();
	const unfilteredProperties = useBatchedProperties();

	const [isFilterLoading, setIsFilterLoading] = useState(false);
	const [applyFiltersOnLoad, setApplyFiltersOnLoad] = useState(false);
	const [filteredProperties, setFilteredProperties] = useState<Properties>();

	const properties = useMemo(() => {
		if (unfilteredProperties.isLoading || !filteredProperties) return unfilteredProperties;

		const allowedById: Record<number, boolean> = {};

		filteredProperties.entries.forEach((entry) => (allowedById[entry.id] = true));

		return {
			...unfilteredProperties,
			features: unfilteredProperties.features.filter((feature) => allowedById[feature.properties.data.id]),
		};
	}, [unfilteredProperties, filteredProperties]);

	const updateFilters = (reset?: boolean) => {
		if (unfilteredProperties.isLoading) {
			setApplyFiltersOnLoad(true);
			return;
		}

		setIsFilterLoading(true);

		if (abortControllerRef.current) abortControllerRef.current.abort();

		abortControllerRef.current = new AbortController();
		const signal = abortControllerRef.current.signal;

		const encodedPolygons: string[] = [];

		if (polygons?.length)
			for (const polygon of polygons) {
				const encoded = polyline.encode(polygon.geometry.coordinates[0] as [number, number][]);

				encodedPolygons.push(encoded);
			}

		const filters: V3PropertyFilter[] = [];
		if (!reset) {
			const queryFilterTerms: [string, Scalar | Scalar[] | undefined][] = [
				["address", address],
				["group_ids", groups],
				["polygons", encodedPolygons],
				["has_images", hasImages],
			];

			const accessorFilterTerms: [Accessor, Scalar | Scalar[] | undefined][] = [
				["building.detachment", detachment],
				["building.property_type", propertyType],
				["building.wall_type", wallType],
				["heating.fuel_type", fuelType],
				[
					useLodgedGrades ? "metrics.lodged_epc_rating_grade" : "metrics.epc_rating_grade",
					grades ? Object.keys(grades).filter((grade) => grades[grade]) : undefined,
				],
				["roof.roof_type", roofType],
				[
					"image_meta.insulation_performance",
					thermalScores
						? Object.keys(thermalScores)
								.filter((score) => thermalScores[score])
								.map(Number)
						: undefined,
				],
				["image_meta.analysis", analysis],
			];

			// Only send filters that have content
			for (const [filter, term] of queryFilterTerms) {
				if (term == null || term === "" || (Array.isArray(term) && term.length === 0)) continue;

				filters.push({ filter, term } as QueryFilter | Filter);
			}
			for (const [accessor, term] of accessorFilterTerms) {
				if (term == null || term === "" || (Array.isArray(term) && term.length === 0)) continue;

				filters.push({ accessor, term } as Filter);
			}
		}

		client.v3
			.getProperties(
				team,
				mapAccessors,
				mapPropertyAttributes,
				filters.length ? filters : undefined,
				undefined,
				false,
				undefined,
				signal
			)
			.then((json) => setFilteredProperties(json))
			.catch((e: Error) => {
				if (e.name === "AbortError") return;

				throw e;
			})
			.finally(() => setIsFilterLoading(false));
	};

	useEffect(() => {
		if (!unfilteredProperties.isLoading && applyFiltersOnLoad) updateFilters();

		// NB: Ignore the deps, it would be better to use useCallback above but this broke the isFilterLoading behaviour
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [unfilteredProperties.isLoading]);

	return {
		properties,
		updateFilters,
		isFilterLoading,
	};
}
